import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useRecoilState } from 'recoil';
import { subAtom, roleAtom } from 'core/config/atoms';

import { FileText, AreaChart, SquareGanttChart, FilePieChart, Bed, NotepadText } from 'lucide-react';

import { AnimatePresence, motion } from "framer-motion";

import PanelReports from 'panels/reports/PanelReports';
import PanelReportItem from 'panels/reports/PanelReportItem';
import PanelReportOrder from 'panels/reports/PanelReportOrder';
import PanelReportInvoice from 'panels/reports/PanelReportInvoice';
import PanelReportHBook from 'panels/reports/PanelReportHBook';
import PanelReportAttendance from 'panels/reports/PanelReportAttendance';
import PanelReportBooking from 'panels/reports/PanelReportBooking';
import PanelReportGST from 'panels/reports/PanelReportGST';
import PanelReportCRM from 'panels/reports/PanelReportCRM';
import PanelReportTRN from 'panels/reports/PanelReportTRN';
import PanelBookReports from 'panels/reports/PanelBookReports';

import PanelDev from 'panels/dev/PanelDev';

export default function LayoutReports() {
    const [sub] = useRecoilState(subAtom);
    const [role] = useRecoilState(roleAtom);

    const [isOpen, setIsOpen] = useState(true);

    const { page } = useParams();

    const navigate = useNavigate();

    const itemVariants = {
        closed: { opacity: 0 },
        open: { opacity: 1 }
    };

    const changeNav = (link) => {
        navigate(`/${link}`);
        setIsOpen(true);
    }

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    }

    const closeMenu = () => {
        setIsOpen(false);
    }

    const openMenu = () => {
        setIsOpen(true);
    }

    return (
        <div className='w-full min-h-screen grid grid-cols-12 gap-2'>
            <AnimatePresence>
                {
                    isOpen && <motion.div initial={{ flexBasis: 0, opacity: 0 }} animate={{ flexBasis: '100%', opacity: 1 }} exit={{ flexBasis: 0, opacity: 0, transition: { delay: 0.2, duration: 0.3 } }} className="col-span-2 h-screen bg-slate-100">
                        <motion.h1 initial="closed" animate="open" exit="closed" variants={itemVariants} className="text-2xl font-medium px-1 mx-2 py-1 my-2 rounded">Reports</motion.h1>
                        <motion.ul initial="closed" animate="open" exit="closed" variants={itemVariants} className="w-full flex flex-col items-start space-y-2 px-2 mt-2">
                            {
                                (sub.features.isRoom && role.modules.includes("BOOKING")) && <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'bookReports' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('bookReports') }}>
                                    <AreaChart size={20} className='mr-2' />Booking Overview Report
                                </li>
                            }
                            {
                                role.modules.includes("TERMINAL") && <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'reports' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('reports') }}>
                                    <AreaChart size={20} className='mr-2' />Food Overview Report
                                </li>
                            }
                            {
                                sub.apps.includes("APP00002") && <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'reportHBook' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('reportHBook') }}>
                                    <FileText size={20} className='mr-2' /> Hostel Booking Report
                                </li>
                            }
                            {
                                role.modules.includes("TERMINAL") && <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'reportItem' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('reportItem') }}>
                                    <SquareGanttChart size={20} className='mr-2' /> Item wise report
                                </li>
                            }
                            {
                                role.modules.includes("TERMINAL") && <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'reportOrder' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('reportOrder') }}>
                                    <FilePieChart size={20} className='mr-2' /> Order wise report
                                </li>
                            }
                            {
                                role.modules.includes("TERMINAL") && <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'reportInvoice' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('reportInvoice') }}>
                                    <FileText size={20} className='mr-2' /> Invoice wise Report
                                </li>
                            }
                            {
                                (sub.features.isRoom && role.modules.includes("BOOKING")) && <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'reportBooking' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('reportBooking') }}>
                                    <NotepadText size={20} className='mr-2' /> Booking report
                                </li>
                            }
                            {
                                <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'reportTrn' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('reportTrn') }}>
                                    <FileText size={20} className='mr-2' /> Transaction Report
                                </li>
                            }
                            {
                                sub.features.isCrm && <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'reportCRM' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('reportCRM') }}>
                                    <FileText size={20} className='mr-2' /> CRM Report
                                </li>
                            }
                            {
                                sub.features.isTax && <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'reportGST' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('reportGST') }}>
                                    <FileText size={20} className='mr-2' /> GST Tax Report
                                </li>
                            }
                            {
                                sub.apps.includes("APP00002") && <li className={`w-full p-1 cursor-pointer text-base hover:bg-gray-200 rounded flex items-center ${page === 'reportAttend' ? 'text-prime font-medium' : 'text-black'}`} onClick={() => { changeNav('reportAttend') }}>
                                    <FileText size={20} className='mr-2' /> Attendance Report
                                </li>
                            }
                        </motion.ul>
                    </motion.div>
                }
            </AnimatePresence>
            <div className={`h-screen ${isOpen ? "col-span-10" : "col-span-12"}`}>
                {
                    (() => {
                        switch (page) {
                            case 'reports':
                                return (<PanelReports onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            case 'bookReports':
                                return (<PanelBookReports onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            case 'reportItem':
                                return (<PanelReportItem onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            case 'reportOrder':
                                return (<PanelReportOrder onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            case 'reportInvoice':
                                return (<PanelReportInvoice onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            case 'reportBooking':
                                return (<PanelReportBooking onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            case 'reportHBook':
                                return (<PanelReportHBook onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            case 'reportAttend':
                                return (<PanelReportAttendance onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            case 'reportGST':
                                return (<PanelReportGST onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            case 'reportCRM':
                                return (<PanelReportCRM onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            case 'reportTrn':
                                return (<PanelReportTRN onToggle={toggleMenu} closeToggle={closeMenu} openToggle={openMenu} />);
                            default:
                                return (<PanelDev />);
                        }
                    })()
                }
            </div>
        </div>
    )
}
